import type { Industry } from "@v2/features/home/components/prompt-suggestions/industries";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  INDUSTRIES,
  INDUSTRY_ICONS,
} from "@v2/features/home/components/prompt-suggestions/industries";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { ChipButton } from "./ChipButton";

export function IndustrySelector({
  onBack,
  onSelectIndustry,
}: {
  onBack: () => void;
  onSelectIndustry: (industry: Industry) => void;
}) {
  const [industrySearch, setIndustrySearch] = useState("");
  const industryChipsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const filteredIndustries = useMemo(() => {
    if (!industrySearch.trim()) {
      return INDUSTRIES;
    }
    const search = industrySearch.toLowerCase();
    return INDUSTRIES.filter((industry) => {
      return industry.toLowerCase().includes(search);
    });
  }, [industrySearch]);

  const checkScroll = useCallback(() => {
    if (industryChipsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = industryChipsRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  }, []);

  useEffect(() => {
    checkScroll();
    const container = industryChipsRef.current;
    if (container) {
      container.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      return () => {
        container.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }
  }, [checkScroll]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIndustrySearch(e.target.value);
      setTimeout(checkScroll, 0);
    },
    [checkScroll],
  );

  const scroll = useCallback((direction: "left" | "right") => {
    if (industryChipsRef.current) {
      const scrollAmount = industryChipsRef.current.clientWidth;
      industryChipsRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  }, []);

  return (
    <div className="relative z-[1]">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted hover:text-default transition-colors cursor-pointer"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Select an industry</span>
        </button>

        <div className="relative w-48">
          <Input
            type="text"
            placeholder="Search"
            value={industrySearch}
            onChange={handleSearchChange}
            startEnhancer={<Search className="w-3.5 h-3.5 text-muted" />}
            size="sm"
          />
        </div>
      </div>

      <div className="relative group/scroll">
        {canScrollRight && (
          <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />
        )}
        {canScrollLeft && (
          <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
        )}
        <div
          ref={industryChipsRef}
          className="flex gap-2 overflow-x-auto no-scrollbar scroll-smooth"
        >
          <AnimatePresence>
            {filteredIndustries.length === 0 ? (
              <span className="text-muted text-sm flex items-center py-3 px-2 select-none w-full justify-center">
                No industries found.
              </span>
            ) : (
              filteredIndustries.map((industry) => (
                <motion.div
                  key={industry}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8, width: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex-shrink-0"
                >
                  <ChipButton
                    onClick={() => onSelectIndustry(industry)}
                    startEnhancer={React.cloneElement(
                      INDUSTRY_ICONS[industry],
                      {
                        className: "w-3.5 h-3.5",
                      },
                    )}
                  >
                    {industry}
                  </ChipButton>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
        <Button
          icon
          variant="secondary"
          size="base"
          onClick={() => scroll("left")}
          UNSAFE_className={twMerge(
            "absolute -left-8 top-1/2 -translate-y-1/2",
            "shadow-md z-10",
            "hidden md:flex",
            !canScrollLeft && "md:hidden",
          )}
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-4 h-4 text-muted" />
        </Button>
        <Button
          icon
          variant="secondary"
          size="base"
          onClick={() => scroll("right")}
          UNSAFE_className={twMerge(
            "absolute -right-8 top-1/2 -translate-y-1/2",
            "shadow-md z-10",
            "hidden md:flex",
            !canScrollRight && "md:hidden",
          )}
          aria-label="Scroll right"
        >
          <ChevronRight className="w-4 h-4 text-muted" />
        </Button>
      </div>
    </div>
  );
}
